import { Injectable, signal, computed, inject } from '@angular/core';
import { PermissionService } from './permission.service';
import {
  PermissionDenial, mergeDenial, allMissingPermissions, denialHeadline,
  hasNamedPermissions, denialFor, deniedPermissionsAlreadyHeld,
} from '../utils/permission-denial.utils';

/**
 * v1.6.1687 — Asana 1216892546346406. The permission 403s seen on the page
 * the operator is currently looking at.
 *
 * Deliberately per-page, not per-session: a denial collected on Orders means
 * nothing once the operator navigates to Products, and leaving it in place
 * would put a stale permission banner on a page that works. `clear()` is
 * called on every successful navigation.
 *
 * Denials ACCUMULATE within a page. The ticket is explicit that several
 * failing requests must produce ONE notice listing every missing permission,
 * rather than one toast per request — which is what a shared collection buys
 * over each screen handling its own error.
 */
@Injectable({ providedIn: 'root' })
export class PermissionDenialService {
  private readonly _denials = signal<PermissionDenial[]>([]);

  /** Every denial on the current page. */
  readonly denials = this._denials.asReadonly();

  /** Anything to show at all. */
  readonly hasDenials = computed(() => this._denials().length > 0);

  /** The one sentence for the notice — the server's own wording where possible. */
  readonly headline = computed(() => denialHeadline(this._denials()));

  /** Distinct permission keys, for the chips. */
  readonly missingPermissions = computed(() => allMissingPermissions(this._denials()));

  /** False for a 403 that named nothing (a disabled plugin, say). */
  readonly hasNamedPermissions = computed(() => hasNamedPermissions(this._denials()));

  /**
   * Record a denial. De-duplicated by URL, so a page that retries the same
   * failing request does not grow the list on every attempt.
   */
  record(denial: PermissionDenial): void {
    // v1.6.1787 — see heldByUser(): a 403 naming only permissions this user
    // HAS is not a permission problem, and saying it is sends them to an
    // administrator who can do nothing.
    if (this.heldByUser(denial)) return;
    this._denials.update(list => mergeDenial(list, denial));
  }

  private perms = inject(PermissionService);

  /**
   * ── v1.6.1787 — Asana 1219658573529681 ────────────────────────────────
   *
   * Angelo, on the ticket: "Still randomly get the pop up at the top telling
   * me i dont have permissions even when i have all perimssions."
   *
   * He is right, and the banner is lying in that case. A 403 can arrive for
   * reasons that have nothing to do with the operator's role — a feature not
   * enabled for the tenant, a store the request did not scope to, a backend
   * check that is simply wrong — and the notice reported every one of them as
   * "ask an administrator for this permission". For a super admin, who holds
   * everything by definition, that advice is impossible to act on.
   *
   * So a denial is dropped when the user already holds every permission it
   * names. What is lost is nothing: a banner that was not true. What remains
   * is the 403 itself, which the screen reports through its own error path
   * like any other failed request.
   *
   * A denial naming NO permission is still shown: it carries the server's own
   * sentence, which may be the only account of what happened.
   */
  private heldByUser(denial: PermissionDenial): boolean {
    return deniedPermissionsAlreadyHeld(denial?.permissions, p => this.perms.has(p));
  }

  // ── v1.6.1814 — export-switch refusals (Asana 1219882772481516) ────────
  //
  // A 403 carrying `data.switch` is NOT a permission problem, so it is kept
  // in its own signal rather than mixed into `_denials`: the permission
  // notice offers "ask an administrator to add the permission to your role",
  // which is wrong advice here — no permission is missing, the platform team
  // switched the tenant's exports off.
  //
  // It shares this service for the one thing it genuinely shares: it is a
  // per-PAGE notice, cleared by the same navigation that clears the others.
  private readonly _exportSwitchNotice = signal<string>('');

  readonly exportSwitchNotice = this._exportSwitchNotice.asReadonly();
  readonly hasExportSwitchNotice = computed(() => this._exportSwitchNotice().length > 0);

  /**
   * Record an export-switch refusal. Last message wins rather than
   * accumulating: the two switches have one message each, and a page that
   * retries must not stack the same sentence.
   */
  recordExportSwitch(message: string): void {
    const m = String(message ?? '').trim();
    if (m) this._exportSwitchNotice.set(m);
  }

  /** Called on navigation — see the class comment. */
  clear(): void {
    if (this._denials().length) this._denials.set([]);
    if (this._exportSwitchNotice()) this._exportSwitchNotice.set('');
  }

  /**
   * The denial for one particular request, so a screen can tell its MAIN
   * request from a side one: a failed main request replaces the list with an
   * empty state carrying the message, while a failed side request leaves the
   * rest of the page working.
   */
  forUrl(urlFragment: string): PermissionDenial | null {
    return denialFor(this._denials(), urlFragment);
  }

  /** True when a specific request was denied — the guard a template uses. */
  wasDenied(urlFragment: string): boolean {
    return this.forUrl(urlFragment) !== null;
  }
}
